import { Box, Typography, Grid, useTheme } from '@mui/material';
import StockCheckCategoryTable from './StockCheckCategoryTable';
import { FC, useMemo } from 'react';
import { TempCategoryItem, TempStockCheckItem, TempCountZoneItem } from '..';
import { Home02 } from '../../../assets';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';

type Props = {
    groupedStockCountItems?: TempCountZoneItem[];
    onItemStatusChange?: (itemId: number | undefined, checked: boolean) => void;
    sortPartialStockCountItems: (zoneIndex: number, categoryIndex: number, fieldToSort: keyof TempStockCheckItem, sortDirection: 'asc' | 'desc') => void;
    togglePartialStockCountItem: (zoneIndex: number, categoryIndex: number, itemNumber: string | undefined) => void;
    isDisabled: boolean;
    isViewMode?: boolean;
    bulkApprovalState?: boolean | null;
    currencyCode: string;
}

const StockCheckContent: FC<Props> = ({
  groupedStockCountItems,
  onItemStatusChange,
  sortPartialStockCountItems,
  togglePartialStockCountItem,
  isDisabled,
  isViewMode,
  bulkApprovalState,
  currencyCode,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const hasLocations = groupedStockCountItems && !(groupedStockCountItems.length === 1 && groupedStockCountItems[0].name === 'No Location');
  const getZoneTotal = (zone: TempCountZoneItem) =>
    zone.categories.reduce((acc, cat) => acc + (cat.items?.length ?? 0), 0);
  const totalItems = groupedStockCountItems?.reduce((acc, zone) => acc + getZoneTotal(zone), 0) ?? 0;

  // Sort zones by rowNumber if exists, otherwise by description
  const sortedZones = useMemo(() => hasLocations
    ? [...(groupedStockCountItems ?? [])].sort((a, b) => {
      if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
        return a.rowNumber - b.rowNumber;
      }
      if (a.rowNumber !== undefined) return -1;
      if (b.rowNumber !== undefined) return 1;
      return (a.name ?? '').localeCompare(b.name ?? '');
    })
    : groupedStockCountItems ?? [], [groupedStockCountItems, hasLocations]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      py={3}
    >
      {/* Top-level total */}
      <Box display="flex"
        flexDirection="row"
        alignItems="center"
        mb={2}
        mt={5}>
        <Pill text={`${totalItems} ${t('totalItems')}`} />
      </Box>
      {
        hasLocations
          ? sortedZones.map((location: TempCountZoneItem, locIdx: number) => (
            <Grid
              container
              key={location.name}
              sx={{
                border: '1px solid',
                boxShadow: '0 1px 2px 1px #1018280D',
                paddingTop: '8px',
                pb: '4px',
                marginTop: '16px',
                borderRadius: '12px',
                borderColor: theme.palette.custom.gray[300],
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Grid item
                xs={12}>
                <Box display="flex"
                  flexDirection="row"
                  p={4}
                  className="title"
                  alignItems="center">
                  <Home02 color={theme.palette.custom.gray[500]} />
                  <Typography sx={{ mb: '1px', ml: '10px' }}>
                    {t('countZone')}: {location.name}
                  </Typography>
                  <Pill text={`${getZoneTotal(location)} items`} />
                </Box>
                {/* Flatten all items from all categories, then sort by rowNumber or description */}
                <StockCheckCategoryTable
                  category={{
                    name: '',
                    items: location.categories
                      .flatMap(cat => cat.items ?? [])
                      .sort((a, b) => {
                        if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
                          return a.rowNumber - b.rowNumber;
                        }
                        if (a.rowNumber !== undefined) return -1;
                        if (b.rowNumber !== undefined) return 1;
                        return (a.description || '').localeCompare(b.description || '');
                      }),
                  }}
                  categoryIndex={0}
                  zoneIndex={locIdx}
                  onItemStatusChange={onItemStatusChange}
                  sortPartialStockCountItems={(field, direction) => sortPartialStockCountItems(locIdx, 0, field, direction)}
                  togglePartialStockCountItem={(itemNumber) => togglePartialStockCountItem(locIdx, 0, itemNumber)}
                  isDisabled={isDisabled}
                  isViewMode={isViewMode}
                  bulkApprovalState={bulkApprovalState}
                  currencyCode={currencyCode}
                />
              </Grid>
            </Grid>
          ))
        // Legacy: group by category, sort categories by name, items by description
          : groupedStockCountItems?.map((location: TempCountZoneItem, locIdx: number) => (
            <Grid
              container
              key={location.name}
              sx={{
                border: '1px solid',
                boxShadow: '0 1px 2px 1px #1018280D',
                paddingTop: '8px',
                pb: '4px',
                marginTop: '16px',
                borderRadius: '12px',
                borderColor: theme.palette.custom.gray[300],
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Grid item
                xs={12}>
                {[...(location.categories ?? [])]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((category: TempCategoryItem, catIdx: number) => (
                    <Box key={category.name}
                      display='flex'
                      flexDirection='column'
                      my={2}>
                      <Box display="flex"
                        flexDirection="column"
                        p={2}>
                        {hasLocations && catIdx === 0 && (
                          <Box display="flex"
                            flexDirection="row"
                            p={4}
                            className="title"
                            alignItems="center">
                            <Home02 color={theme.palette.custom.gray[500]} />
                            <Typography sx={{ mb: '1px', ml: '10px' }}>
                              {t('countZone')}: {location.name}
                            </Typography>
                            <Pill text={`${getZoneTotal(location)} ${t('items')}`} />
                          </Box>
                        )}
                        <Box display="flex"
                          flexDirection="row"
                          p={4}
                          alignItems="center">
                          <Typography sx={{ mt: hasLocations && catIdx === 0 ? 1 : 0 }}>
                            {category.name}
                          </Typography>
                          <Pill text={`${category.items.length} ${t('items')}`} />
                        </Box>
                      </Box>
                      <StockCheckCategoryTable
                        category={{
                          ...category,
                          items: [...(category.items ?? [])].sort((a, b) =>
                            (a.description || '').localeCompare(b.description || ''),
                          ),
                        }}
                        categoryIndex={catIdx}
                        zoneIndex={locIdx}
                        onItemStatusChange={onItemStatusChange}
                        sortPartialStockCountItems={(field, direction) => sortPartialStockCountItems(locIdx, catIdx, field, direction)}
                        togglePartialStockCountItem={(itemNumber) => togglePartialStockCountItem(locIdx, catIdx, itemNumber)}
                        isDisabled={isDisabled}
                        isViewMode={isViewMode}
                        bulkApprovalState={bulkApprovalState}
                        currencyCode={currencyCode}
                      />
                    </Box>
                  ))}
              </Grid>
            </Grid>
          ))
      }
    </Box>
  );
};

export default StockCheckContent;
